// A&M Design System
// Clean white cards on warm paper, near-black text, A&M-red accent
// Jakarta Sans headings (extrabold 800), JetBrains Mono numerals

export const amDesignColors = {
  // Core palette
  paper: '#F7F6F3',           // Warm paper background
  white: '#FFFFFF',           // Clean white cards
  text: {
    primary: '#1A1A1A',       // Near-black text
    secondary: '#666666',     // Secondary text
    tertiary: '#999999',      // Tertiary text
  },
  primary: '#C80F2E',         // A&M-red accent
  primaryLight: '#E8344A',    // Lighter red for hovers
  primaryDark: '#A00822',     // Darker red for active
  border: '#E8E8E8',          // Light borders
  divider: '#F0F0F0',         // Divider color
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#C80F2E',
  info: '#2196F3',
  ghost: '#F5F5F5',           // Ghost background
};

export const amDesignTypography = {
  fontFamily: {
    heading: 'Jakarta Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    body: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    mono: '"JetBrains Mono", "Courier New", monospace',
  },
  h1: {
    fontSize: '2.5rem',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.5rem',
    fontWeight: 800,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  h4: {
    fontSize: '1.25rem',
    fontWeight: 800,
    letterSpacing: '-0.005em',
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.02em',
  },
};

export const amDesignComponents = {
  card: {
    backgroundColor: amDesignColors.white,
    borderRadius: '14px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
    border: `1px solid ${amDesignColors.border}`,
  },
  cardLarge: {
    backgroundColor: amDesignColors.white,
    borderRadius: '14px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
    border: `1px solid ${amDesignColors.border}`,
  },
  input: {
    backgroundColor: amDesignColors.paper,
    borderRadius: '10px',
    border: `1px solid ${amDesignColors.border}`,
    '&:focus-within': {
      borderColor: amDesignColors.primary,
    },
  },
  button: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '10px',
    fontSize: '0.875rem',
  },
  buttonPrimary: {
    backgroundColor: amDesignColors.primary,
    color: amDesignColors.white,
    '&:hover': {
      backgroundColor: amDesignColors.primaryDark,
    },
  },
  buttonSecondary: {
    backgroundColor: amDesignColors.paper,
    color: amDesignColors.text.primary,
    border: `1px solid ${amDesignColors.border}`,
    '&:hover': {
      backgroundColor: amDesignColors.ghost,
      borderColor: amDesignColors.primary,
    },
  },
  buttonGhost: {
    backgroundColor: 'transparent',
    color: amDesignColors.primary,
    border: `1px solid ${amDesignColors.primary}`,
    '&:hover': {
      backgroundColor: `${amDesignColors.primary}10`,
    },
  },
  chip: {
    backgroundColor: amDesignColors.paper,
    color: amDesignColors.text.primary,
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  chipPrimary: {
    backgroundColor: `${amDesignColors.primary}15`,
    color: amDesignColors.primary,
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
};

// MUI Theme overrides
export const amTheme = {
  palette: {
    mode: 'light',
    primary: {
      main: amDesignColors.primary,
      light: amDesignColors.primaryLight,
      dark: amDesignColors.primaryDark,
    },
    background: {
      default: amDesignColors.paper,
      paper: amDesignColors.white,
    },
    text: {
      primary: amDesignColors.text.primary,
      secondary: amDesignColors.text.secondary,
    },
    divider: amDesignColors.divider,
  },
  typography: {
    fontFamily: amDesignTypography.fontFamily.body,
    h1: {
      ...amDesignTypography.h1,
      fontFamily: amDesignTypography.fontFamily.heading,
    },
    h2: {
      ...amDesignTypography.h2,
      fontFamily: amDesignTypography.fontFamily.heading,
    },
    h3: {
      ...amDesignTypography.h3,
      fontFamily: amDesignTypography.fontFamily.heading,
    },
    h4: {
      ...amDesignTypography.h4,
      fontFamily: amDesignTypography.fontFamily.heading,
    },
    h5: {
      fontWeight: 700,
      fontSize: '1rem',
      fontFamily: amDesignTypography.fontFamily.heading,
    },
    h6: {
      fontWeight: 700,
      fontSize: '0.875rem',
      fontFamily: amDesignTypography.fontFamily.heading,
    },
    body1: amDesignTypography.body1,
    body2: amDesignTypography.body2,
    caption: amDesignTypography.caption,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          ...amDesignComponents.card,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: amDesignComponents.button,
        containedPrimary: amDesignComponents.buttonPrimary,
        outlined: amDesignComponents.buttonSecondary,
      },
    },
    MuiChip: {
      styleOverrides: {
        root: amDesignComponents.chip,
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: amDesignColors.paper,
          },
        },
      },
    },
  },
};
